import { useEffect, useRef, useState, type CSSProperties } from "react"

// Exposes a ref'd element's viewport top as CSS custom properties so
// descendants can do `calc(100dvh - var(--outerTop))` (fill from the element
// down) or `calc(100dvh - var(--topOffset))` (fill from just below the tab
// strip). Re-measures on body/window resize.
//
// Intentionally uses `useEffect` (not `useLayoutEffect`) — measuring during
// the layout phase can cascade into a `flushSync` warning when a sibling
// component (e.g. a Radix popover) re-renders during the same pass.
export function useViewportFill() {
  const ref = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState<number | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let rafId = 0
    const measure = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => setTop(el.getBoundingClientRect().top))
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(document.body)

    const mo = new MutationObserver(measure)
    let ancestor: HTMLElement | null = el.parentElement
    while (ancestor) {
      mo.observe(ancestor, { childList: true })
      if (ancestor === document.body) break
      ancestor = ancestor.parentElement
    }

    window.addEventListener("resize", measure)

    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
      mo.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [])

  const style: CSSProperties =
    top != null
      ? ({
          "--outerTop": `${top}px`,
          "--topOffset": `${top + TAB_STRIP_HEIGHT}px`,
        } as CSSProperties)
      : {}

  return { ref, style }
}

const TAB_STRIP_HEIGHT = 37
